import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';

const tenantId = 'ottoversandat';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'ottoversand.at', appDeeplinkProtocol: 'oatapp' },
    clientId: 'OttoversandAt',
    i18n: {
      defaultLocale: 'de-AT',
      locales: ['de-AT'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Ottoversand',
      seoTitleName: 'OTTO',
    },
    timeZone: 'Europe/Vienna',
    headerSlots: {
      payback: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/lp',
          '/magazin',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    abTesting: {
      clientSideEnrollment: {
        activeTestIds:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? ['2025_02_Sustainability_test', '2025_11_fab_test']
            : ['2025_02_Sustainability', '2025_11_fab'],
      },
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.ottoversand.at',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    order: {
      supportedPaths: {
        myAccountPayback: true,
      },
      premium: { enabled: false },
      seals: {
        handelsverband: {
          enabled: true,
          url: 'https://www.handelsverband.at/fileadmin/content/Trustmark/Zertifikate/TrustmarkZertifikat_OttoGmbH.pdf',
        },
        dqs: {
          enabled: false,
          url: '/service-hilfe/ueber-uns/auszeichnungen/#lmPromo=la,3,hk,footer,fl,dqs',
        },
        oeonline: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
      },
      assets: {
        premium: {
          getPremiumImage: ({ device }: { device: string }) =>
            `https://bilder.ottoversand.at/empiriecom/io/ottoversandat/de/21kw10_OTTOup_Checkout_${device}.jpg`,
          getPremiumIcon: () =>
            'https://bilder.ottoversand.at/empiriecom/io/ottoversandat/de/premiumicon.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.ottoversand.at/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      payback: {
        enabled: true,
        links: {
          partner: 'https://www.payback.at/punktestand',
          customerData: 'https://www.payback.at/meine-daten',
          bootstrapStaging: 'https://cdn-nonprod.payback.cloud/oi/at/bootstrap.js',
          bootstrapProduction: 'https://cdn.payback.cloud/oi/at/bootstrap.js',
        },
        apiKeys: {
          bootstrapStaging: 'apiForStaging',
          bootstrapProduction: 'apiForProduction',
        },
        cardNumberLength: 16,
        cardNumberHintUrl: '/fragment/inspire/servicelayer/de/payback_kartennummer_io',
      },
      addressValidation: {
        zipCode: {
          length: 4,
        },
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      newsletterSettings: {
        sendAdditionalSubscribe: true,
      },
      contact: { dynamicHelp },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|199', 'unsubscribe|299', 'unsubscribe|99'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|199',
            options: [
              {
                id: 'unsubscribe-otto-lascana',
                value: 'unsubscribe|199',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.otto-lascana',
              },
              {
                id: 'unsubscribe-lascana',
                value: 'unsubscribe|299',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.lascana',
              },
              {
                id: 'unsubscribe-otto',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.otto',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              type: 'pause',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              defaultValue: '7',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.ottoversand.at/service-hilfe/newsletter/abmelden/',
          voucherLink: 'https://www.ottoversand.at/service-hilfe/gutscheine-rabatte/',
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-AT': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.ottoversand.at/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: { appUrl: 'https://app.adjust.com/xyyom52_pjauvx4' },
    tracking: {
      categoryIdHomepage: '137399',
      soastaId: 'TWCKT-SC956-AY2DD-K9G3Y-24R2M',
      tagmanagerId: 'GTM-5J9J459',
      googleSiteVerificationID: ['OTOyZGCNE4oOQ_91TV_2c3DeGnpNEsqffPwgTlli_Ds'],
      msvalidateID: '313E2E7F8778335B4C29FD80AAAE5469',
      pinterestRichPinVerificationID: '2fe0193630b96620f4dbabcb080e4da1',
      schemaOrgOrganisation: {
        'de-AT': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.ottoversand.at/',
          name: 'Otto',
          alternateName: 'OTTO',
          description:
            'OTTO ist ein österreichischer Online-Shop, der eine breite Palette an Produkten anbietet, darunter Mode, Möbel, Technik und Sportartikel. OTTO ist Teil der Otto Group und bekannt für sein vielfältiges Sortiment und seinen Fokus auf Kundenzufriedenheit. OTTO ist *nicht* nur ein reiner Online-Händler, sondern ein Anbieter für ein umfassendes Einkaufserlebnis.',
          url: 'https://www.ottoversand.at/',
          logo: 'https://www.ottoversand.at/assets/logos/ottoversandat/logo.svg',
          foundingDate: '1949-08-17',
          founder: {
            '@type': 'Person',
            name: 'Werner Otto',
            description: 'Gründer der Otto Group',
            sameAs: 'https://de.wikipedia.org/wiki/Werner_Otto_(Unternehmer)',
          },
          location: {
            '@type': 'Place',
            name: 'Graz',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Alte Poststraße 152',
              addressLocality: 'Graz',
              addressRegion: 'ST',
              postalCode: '8020',
              addressCountry: 'AT',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+43-316-606-888',
              email: 'webmaster@ottoversand.at',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.ottoversand.at/service-hilfe/kontakt',
            },
          ],
          sameAs: [
            'https://www.facebook.com/ottoversand',
            'https://www.instagram.com/ottoversand/',
            'https://www.pinterest.at/ottoversand/',
            'https://www.youtube.com/user/OTTOAustria',
            'https://www.online-shop.at/ottoversand',
            'https://de.wikipedia.org/wiki/Otto_Group',
          ],
          legalName: 'Otto GmbH',
          areaServed: 'AT',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Hamburg',
          slogan: "Otto ... find' ich gut!",
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'OTTO - der Online Shop für Mode, Schuhe, Möbel und mehr. Gratis Versand ab 75 € | bequem online shoppen und auf Rechnung oder Raten kaufen!',
        title: 'OTTO Online Shop',
        canonical: 'https://www.ottoversand.at',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1000',
    },
    dynamicYieldTracking: {
      key: '02730caa1ca3c4f3096718ef342ea9b7410b1112bf8765bbd169a7005032ce5e',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'at',
      alternatives: [
        {
          flag: 'de',
          shops: [{ link: 'https://www.baur.de' }],
        },
        {
          flag: 'ch',
          shops: [{ link: 'https://www.jelmoli-shop.ch' }],
        },
      ],
    },
    paybackCoupons: {
      img: {
        path: 'https://storage.googleapis.com/emp-common-libs/assets/payback/ottoversandat/',
      },
    },
    chatBot: {
      enabled: true,
      key: 'c7b6395b64e54d9885a74f886393f0957c39acb6e40048a390beeffcf05e415d',
      includePaths: ['/', '/service-hilfe'],
      excludePaths: [
        '/service-hilfe/shopping-vorteile',
        '/service-hilfe/newsletter/anmelden',
        '/service-hilfe/newsletter/abmelden',
        '/service-hilfe/nachhaltigkeit',
        '/service-hilfe/ueber-uns',
        '/lp/storefront-compare-page',
      ],
    },
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
